/**
 * Reusable rich dashboard widgets (Payroll Status, Recruitment Pipeline,
 * Performance Snapshot, Workforce by Location, Today's Schedule,
 * Team Activity, Organization Preview, Upcoming Meetings, etc.) used in
 * the `extraRows` slot of DashboardShell so HR / Manager / Employee /
 * Admin dashboards stay rich and never look empty.
 */
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  CheckCircle2, ArrowRight, BarChart3, MapPin, Calendar as CalendarIcon,
  TrendingUp, Star, Users, FileText, Wallet, Sparkles, Award, Briefcase,
} from "lucide-react";
import { WidgetCard, DonutChart } from "@/components/app/DashboardShell";
import { avatarFor, initialsOf } from "@/lib/avatar";

/* ---------------- Payroll Status (timeline pill) ---------------- */
export function PayrollStatusWidget({ stage = "Approved", month = "Mar 2026" }) {
  const steps = ["Data Locked", "Verified", "Approved", "Processed"];
  const idx = Math.max(0, steps.indexOf(stage));
  return (
    <WidgetCard title="Payroll Status" action={<span className="text-[11px] font-semibold text-slate-500">{month}</span>} testId="w-payroll-status">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 className="w-4 h-4" /></div>
        <div>
          <div className="text-[14px] font-bold text-slate-900 font-[Outfit]">Payroll Ready</div>
          <div className="text-[10.5px] text-slate-500">Your payroll for {month} is ready for review.</div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-2">
        {steps.map((s, i) => (
          <div key={s} className="flex-1 flex flex-col items-center relative">
            {i < steps.length - 1 && <div className={`absolute top-3 left-1/2 right-0 h-0.5 ${i < idx ? "bg-emerald-400" : "bg-slate-200"}`} />}
            <div className={`relative w-6 h-6 rounded-full flex items-center justify-center z-10 ${i <= idx ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>
              {i <= idx ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="text-[9px] font-bold">{i + 1}</span>}
            </div>
            <span className="text-[9.5px] text-slate-500 mt-1.5 text-center font-semibold">{s}</span>
          </div>
        ))}
      </div>
      <Link to="/payroll" className="text-[11px] font-semibold text-violet-700 hover:underline mt-4 inline-block">View payroll dashboard →</Link>
    </WidgetCard>
  );
}

/* ---------------- Recruitment Pipeline (funnel) ---------------- */
export function RecruitmentPipelineWidget({ data = [
  { stage: "Applied",   count: 128, delta: "+12%" },
  { stage: "Screening", count: 62,  delta: "+5%" },
  { stage: "Interview", count: 28,  delta: "-8%" },
  { stage: "Offer",     count: 12,  delta: "+9%" },
  { stage: "Hired",     count: 7,   delta: "+16%" },
] }) {
  const max = Math.max(...data.map(d => d.count), 1);
  const colors = ["#7C3AED", "#3B82F6", "#06B6D4", "#F59E0B", "#EF4444"];
  return (
    <WidgetCard title="Recruitment Pipeline" action={<Link to="/recruitment" className="text-[11px] font-semibold text-violet-700 hover:underline">View all jobs →</Link>} testId="w-recruitment">
      <div className="flex gap-4 items-center">
        <div className="flex flex-col items-center gap-1.5">
          {data.map((d, i) => {
            const w = Math.max(40, (d.count / max) * 130);
            return (
              <div
                key={d.stage}
                className="flex items-center justify-center text-white text-[10.5px] font-bold h-7 rounded shrink-0"
                style={{ width: w, background: colors[i] }}
              >
                {d.count}
              </div>
            );
          })}
        </div>
        <div className="flex-1 space-y-2">
          {data.map((d, i) => (
            <div key={d.stage} className="flex items-center justify-between text-[11.5px]">
              <span className="text-slate-700 font-semibold">{d.stage}</span>
              <span className="flex items-center gap-1.5">
                <span className="text-slate-900 font-bold">{d.count}</span>
                <span className={`text-[10px] font-bold ${d.delta.startsWith("+") ? "text-emerald-600" : "text-rose-600"}`}>{d.delta}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </WidgetCard>
  );
}

/* ---------------- Performance Snapshot ---------------- */
export function PerformanceSnapshotWidget({ overall = 4.2, distribution = [34, 45, 15, 4, 2], quarter = "Q1 2026" }) {
  return (
    <WidgetCard title="Performance Snapshot" action={<span className="text-[11px] font-semibold text-slate-500">{quarter}</span>} testId="w-performance">
      <div className="flex items-center gap-4">
        <DonutChart size={120} thickness={16} data={[{ value: overall * 20, color: "#7C3AED" }, { value: 100 - overall * 20, color: "#F1F5F9" }]} centerValue={overall} centerLabel="/5" />
        <div className="flex-1 space-y-1.5">
          {distribution.map((pct, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px]">
              <span className="w-8 flex items-center gap-0.5 font-bold text-slate-700">{5 - i}<Star className="w-3 h-3 text-amber-400 fill-amber-400" /></span>
              <div className="flex-1 h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-violet-500" style={{ width: `${pct}%` }} /></div>
              <span className="text-[10px] text-slate-500 font-bold w-9 text-right">{pct}%</span>
            </div>
          ))}
        </div>
      </div>
      <Link to="/performance" className="text-[11px] font-semibold text-violet-700 hover:underline mt-3 inline-block">View performance analytics →</Link>
    </WidgetCard>
  );
}

/* ---------------- Workforce by Location ---------------- */
export function WorkforceByLocationWidget({ data = [] }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
  const colors = ["#10B981", "#3B82F6", "#7C3AED", "#F59E0B", "#EF4444"];
  return (
    <WidgetCard title="Workforce by Location" action={<Link to="/people" className="text-[11px] font-semibold text-violet-700 hover:underline">View report →</Link>} testId="w-workforce">
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-full bg-slate-50 flex items-center justify-center shrink-0"><MapPin className="w-9 h-9 text-violet-400" /></div>
        <div className="flex-1 space-y-1.5">
          {(data.length ? data : [{ label: "—", value: 0 }]).slice(0, 5).map((d, i) => (
            <div key={d.label} className="flex items-center gap-2 text-[11.5px]">
              <span className="w-2 h-2 rounded-full" style={{ background: d.color || colors[i] }} />
              <span className="text-slate-700 font-semibold flex-1 truncate">{d.label}</span>
              <span className="text-slate-900 font-bold tabular-nums">{d.value}</span>
              <span className="text-[10px] text-slate-500 w-10 text-right tabular-nums">{Math.round((d.value / total) * 100)}%</span>
            </div>
          ))}
        </div>
      </div>
    </WidgetCard>
  );
}

/* ---------------- Today's Schedule ---------------- */
export function TodaysScheduleWidget({ items }) {
  const data = items || [
    { time: "10:00 AM", title: "Leadership Sync",      duration: "30m" },
    { time: "11:30 AM", title: "HR Strategy Review",   duration: "45m" },
    { time: "02:00 PM", title: "Quarterly Business",   duration: "1h"  },
    { time: "04:00 PM", title: "1:1 with Aarav",       duration: "30m" },
  ];
  return (
    <WidgetCard title="Today's Schedule" action={<Link to="/calendar" className="text-[11px] font-semibold text-violet-700 hover:underline">View calendar →</Link>} testId="w-schedule">
      <div className="space-y-3">
        {data.map((m, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="text-[11px] font-bold text-slate-900 w-16 shrink-0 mt-0.5">{m.time}</div>
            <div className="w-px h-7 bg-slate-200 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-slate-900 truncate">{m.title}</div>
            </div>
            <span className="text-[10px] text-slate-500 shrink-0">{m.duration}</span>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}

/* ---------------- Team Activity ---------------- */
export function TeamActivityWidget({ items, employees = [] }) {
  const data = items || [
    { name: "Aarav Khanna",   action: "Onboarding completed",      time: "2m ago" },
    { name: "Diya Sharma",    action: "Expense submitted",         time: "15m ago" },
    { name: "Vihaan Mehta",   action: "Leave request approved",    time: "1h ago" },
    { name: "Anya Iyer",      action: "Performance review completed", time: "2h ago" },
    { name: "Diya Sharma",    action: "New document uploaded",     time: "3h ago" },
  ];
  return (
    <WidgetCard title="Team Activity" action={<Link to="/announcements" className="text-[11px] font-semibold text-violet-700 hover:underline">View all →</Link>} testId="w-activity">
      <div className="space-y-3">
        {data.map((a, i) => {
          const emp = employees.find(e => e.full_name === a.name) || { full_name: a.name };
          return (
            <div key={i} className="flex items-center gap-3">
              <Avatar className="w-9 h-9"><AvatarImage src={avatarFor(emp)} /><AvatarFallback className="text-[10px]">{initialsOf(a.name)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-bold text-slate-900 truncate">{a.name}</div>
                <div className="text-[10.5px] text-slate-500 truncate">{a.action}</div>
              </div>
              <span className="text-[10px] text-slate-400 shrink-0">{a.time}</span>
            </div>
          );
        })}
      </div>
    </WidgetCard>
  );
}

/* ---------------- Org Preview (small org chart) ---------------- */
export function OrgPreviewWidget({ root, leaders = [] }) {
  const top = root || { full_name: "Aarav Khanna", designation: "CEO & Co-founder" };
  return (
    <WidgetCard title="Organization Preview" action={<Link to="/people" className="text-[11px] font-semibold text-violet-700 hover:underline">View org chart →</Link>} testId="w-org">
      <div className="flex justify-center mb-3">
        <div className="text-center px-4 py-2 rounded-xl border border-violet-100 bg-violet-50/50 inline-flex items-center gap-2 shadow-sm">
          <Avatar className="w-7 h-7"><AvatarImage src={avatarFor(top)} /><AvatarFallback className="text-[9px]">{initialsOf(top.full_name)}</AvatarFallback></Avatar>
          <div className="text-left">
            <div className="text-[11.5px] font-bold text-slate-900">{top.full_name}</div>
            <div className="text-[9.5px] text-slate-500">{top.designation}</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {leaders.slice(0, 4).map(l => (
          <div key={l.id || l.full_name} className="text-center p-2 rounded-lg border border-slate-100 hover:border-violet-200 transition">
            <Avatar className="w-9 h-9 mx-auto"><AvatarImage src={avatarFor(l)} /><AvatarFallback className="text-[10px]">{initialsOf(l.full_name)}</AvatarFallback></Avatar>
            <div className="text-[11px] font-bold text-slate-900 mt-1.5 truncate">{l.full_name}</div>
            <div className="text-[9px] text-slate-500 truncate">{l.designation || "—"}</div>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}

/* ---------------- Qrew Inbox / Approvals queue ---------------- */
export function QrewInboxWidget({ items = [], pending = 0 }) {
  return (
    <WidgetCard title="Qrew Inbox" action={pending > 0 ? <span className="text-[10px] uppercase font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full">{pending} Pending</span> : <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">All clear</span>} testId="w-inbox">
      {items.length === 0 ? (
        <div className="text-[12px] text-slate-500 py-6 text-center">No items in your inbox.</div>
      ) : (
        <div className="space-y-2">
          {items.slice(0, 4).map((it, i) => (
            <div key={i} className="flex items-center gap-3 p-2 rounded-lg border border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center"><Briefcase className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold text-slate-900 truncate">{it.title}</div>
                <div className="text-[10px] text-slate-500 truncate">{it.subtitle || ""}</div>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
            </div>
          ))}
        </div>
      )}
      <Link to="/approvals" className="text-[11px] font-semibold text-violet-700 hover:underline mt-3 inline-block">View all approvals →</Link>
    </WidgetCard>
  );
}

/* ---------------- Recent Payslips (employee) ---------------- */
export function RecentPayslipsWidget({ payslips = [] }) {
  return (
    <WidgetCard title="My Recent Payslips" action={<Link to="/payroll" className="text-[11px] font-semibold text-violet-700 hover:underline">All slips →</Link>} testId="w-payslips">
      {payslips.length === 0 ? (
        <div className="text-[12px] text-slate-500 py-6 text-center">Your first payslip will appear here.</div>
      ) : (
        <div className="space-y-2">
          {payslips.slice(0, 4).map((p, i) => (
            <div key={p.id || i} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100">
              <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><Wallet className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold text-slate-900 truncate">{p.month_label || p.month || "—"}</div>
                <div className="text-[10.5px] text-slate-500">Net ₹{p.net || 0}</div>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}

/* ---------------- AI Nudge / Tip ---------------- */
export function AiTipWidget({ tip = "Try Qrew AI", body = "Ask 'What's my leave balance?' or 'Draft a leave request'", to = "/ai", cta = "Open AI Assistant" }) {
  return (
    <WidgetCard title="AI Assistant" action={<Sparkles className="w-4 h-4 text-violet-500" />} testId="w-ai">
      <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-fuchsia-50 p-4">
        <div className="text-[13px] font-bold font-[Outfit] text-slate-900">{tip}</div>
        <div className="text-[11.5px] text-slate-600 mt-1">{body}</div>
        <Link to={to} className="px-4 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold mt-3 inline-flex items-center gap-1.5">
          {cta} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </WidgetCard>
  );
}
